import React, { useEffect, useState } from 'react';
import { collection, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { PaymentMethod } from '../../../types/financial';
import { useNavigate } from 'react-router-dom';
import './PaymentRecords.css';

interface PaymentRecord {
  id: string;
  firstName: string;
  lastName: string;
  placeCode: string;
  amount: number;
  convenienceFee?: number;
  method: PaymentMethod;
  paymentDate: Date;
  notes?: string;
  registrationId: string;
}

const PaymentRecords: React.FC = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPaymentType, setSelectedPaymentType] = useState<PaymentMethod | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<'date' | 'name' | 'amount' | 'method'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    fetchPaymentRecords();
  }, []);

  const fetchPaymentRecords = async () => {
    try {
      setLoading(true);
      setError(null);
      const snapshot = await getDocs(collection(db, 'Registrations'));
      const paymentRecords: PaymentRecord[] = [];

      console.log(`Found ${snapshot.size} registrations`);
      
      snapshot.forEach(docSnap => {
        const data = docSnap.data();
        console.log(`Processing document ${docSnap.id}:`, data);
        
        // Check if the document has paymentHistory
        const payments = data.paymentHistory || data.payments || [];
        if (payments && Array.isArray(payments) && payments.length > 0) {
          console.log(`Document ${docSnap.id} has ${payments.length} payments:`, payments);
          
          // Extract name from different possible structures
          const primaryCamperName = data.registrationInfo?.primaryCamperName || '';
          const nameParts = primaryCamperName.split(' ');
          const firstName = nameParts[0] || data.firstName || '';
          const lastName = nameParts.slice(1).join(' ') || data.lastName || '';
          const placeCode = data.financial?.accommodationFees?.priceCode || '';

          // Process each payment in the payments array
          payments.forEach((payment: any) => {
            // Convert Firestore timestamp to Date if needed
            let paymentDate: Date;
            if (payment.paymentDate instanceof Timestamp) {
              paymentDate = payment.paymentDate.toDate();
            } else if (payment.paymentDate instanceof Date) {
              paymentDate = payment.paymentDate;
            } else if (payment.date) {
              // Alternative field name
              paymentDate = payment.date instanceof Timestamp ? payment.date.toDate() : new Date(payment.date);
            } else if (payment.paymentDate) {
              // If it's a string or other format, try to parse it
              paymentDate = new Date(payment.paymentDate as string | number);
            } else {
              // Fallback to current date if no date field found
              paymentDate = new Date();
            }

            paymentRecords.push({
              id: `${docSnap.id}-${paymentRecords.length}`,
              firstName,
              lastName,
              placeCode,
              amount: payment.amount,
              convenienceFee: payment.convenienceFee || 0,
              method: payment.method,
              paymentDate,
              notes: payment.notes || payment.referenceNote,
              registrationId: docSnap.id
            });
          });
        }
      });

      setRecords(paymentRecords);
    } catch (err) {
      console.error('Error fetching payment records:', err);
      setError('Failed to load payment records');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredAndSortedRecords = () => {
    let filtered = records;

    // Filter by payment type (normalize to handle both enum and display label formats)
    if (selectedPaymentType !== 'all') {
      filtered = filtered.filter(record => normalizePaymentMethod(record.method) === selectedPaymentType);
    }

    // Filter by search term (name or place code)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(record => 
        `${record.firstName} ${record.lastName}`.toLowerCase().includes(term) ||
        record.placeCode.toLowerCase().includes(term)
      );
    }

    // Sort records
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;

      switch (sortField) {
        case 'date':
          aValue = a.paymentDate;
          bValue = b.paymentDate;
          break;
        case 'name':
          aValue = `${a.firstName} ${a.lastName}`.toLowerCase();
          bValue = `${b.firstName} ${b.lastName}`.toLowerCase();
          break;
        case 'amount':
          aValue = a.amount;
          bValue = b.amount;
          break;
        case 'method':
          aValue = a.method;
          bValue = b.method;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  };

  const handleSort = (field: 'date' | 'name' | 'amount' | 'method') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount);
  };

  const formatAmountWithFee = (record: PaymentRecord): React.ReactElement => {
    const totalAmount = Number(record.amount) || 0;
    if (record.convenienceFee && record.convenienceFee > 0) {
      return (
        <span>
          {formatCurrency(totalAmount)}
          <span style={{ fontSize: '0.85em', color: '#6c757d', marginLeft: '4px' }}>
            (incl {formatCurrency(record.convenienceFee)} fee)
          </span>
        </span>
      );
    }
    return <span>{formatCurrency(totalAmount)}</span>;
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Normalize payment method to handle both enum values and display labels stored in DB
  const normalizePaymentMethod = (method: string): PaymentMethod => {
    const normalized = method?.toLowerCase().replace(/[^a-z]/g, '') || '';
    switch (normalized) {
      case 'cashcheck':
      case 'cash':
      case 'check':
        return 'CashCheck';
      case 'etransfer':
      case 'etransfer':
        return 'Etransfer';
      case 'par':
        return 'Par';
      case 'creditcard':
        return 'CreditCard';
      case 'monerispinpad':
        return 'MonerisPinPad';
      case 'programcmte':
      case 'programcommittee':
        return 'ProgramCmte';
      case 'benevolentfund':
        return 'BenevolentFund';
      case 'other':
        return 'Other';
      default:
        return 'Other';
    }
  };

  const getPaymentMethodLabel = (method: PaymentMethod | string): string => {
    const normalized = normalizePaymentMethod(method);
    switch (normalized) {
      case 'CashCheck': return 'Cash/Check';
      case 'Etransfer': return 'E-transfer';
      case 'Par': return 'PAR';
      case 'CreditCard': return 'Credit Card';
      case 'MonerisPinPad': return 'Moneris PinPad';
      case 'ProgramCmte': return 'Program Committee';
      case 'BenevolentFund': return 'Benevolent Fund';
      case 'Other': return 'Other';
      default: return method;
    }
  };

  const getTotalAmount = () => {
    return getFilteredAndSortedRecords().reduce((sum, record) => sum + (Number(record.amount) || 0), 0);
  };

  const getPaymentTypeTotals = () => {
    const totals: Record<PaymentMethod, number> = {
      CashCheck: 0,
      Etransfer: 0,
      Par: 0,
      CreditCard: 0,
      MonerisPinPad: 0,
      ProgramCmte: 0,
      BenevolentFund: 0,
      Other: 0
    };

    // Use all records, not filtered, so breakdown always shows full totals (including convenience fees)
    records.forEach(record => {
      const normalizedMethod = normalizePaymentMethod(record.method);
      totals[normalizedMethod] += Number(record.amount) || 0;
    });

    return totals;
  };

  const getTotalCreditCardFees = () => {
    return records.reduce((sum, record) => {
      if (normalizePaymentMethod(record.method) === 'CreditCard') {
        return sum + (record.convenienceFee || 0);
      }
      return sum;
    }, 0);
  };

  const handleRowClick = (record: PaymentRecord) => {
    if (record.registrationId) {
      navigate(`/admin/registration-details/${record.registrationId}`);
    }
  };

  const exportToCSV = () => {
    const filteredRecords = getFilteredAndSortedRecords();
    
    // Create CSV header
    const headers = ['First Name', 'Last Name', 'Place Code', 'Amount', 'Conv Fee', 'Total', 'Payment Type', 'Payment Date', 'Notes'];
    
    // Create CSV rows
    const csvRows = [
      headers.join(','),
      ...filteredRecords.map(record => {
        const totalAmount = Number(record.amount) || 0;
        return [
          `"${record.firstName}"`,
          `"${record.lastName}"`,
          `"${record.placeCode}"`,
          record.amount.toFixed(2),
          (record.convenienceFee || 0).toFixed(2),
          totalAmount.toFixed(2),
          `"${getPaymentMethodLabel(record.method)}"`,
          `"${formatDate(record.paymentDate)}"`,
          `"${record.notes || ''}"`
        ].join(',');
      })
    ];
    
    const csvContent = csvRows.join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `payment-records-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return <div>Loading payment records...</div>;
  if (error) return <div className="error">{error}</div>;

  const filteredRecords = getFilteredAndSortedRecords();
  const totalAmount = getTotalAmount();
  const paymentTypeTotals = getPaymentTypeTotals();

  return (
    <div className="payment-records-report">
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '20px'
      }}>
        <h2>Payment Records</h2>
        {filteredRecords.length > 0 && (
          <button 
            style={{
              padding: '8px 16px',
              background: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '0.9rem',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#218838'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#28a745'}
            onClick={exportToCSV}
          >
            Export to CSV
          </button>
        )}
      </div>
      
      {/* Filters and Controls */}
      <div className="filters-section">
        <div className="filter-row">
          <div className="filter-group">
            <label htmlFor="paymentType">Payment Type:</label>
            <select
              id="paymentType"
              value={selectedPaymentType}
              onChange={(e) => setSelectedPaymentType(e.target.value as PaymentMethod | 'all')}
            >
              <option value="all">All Payment Types</option>
              <option value="CashCheck">Cash/Check</option>
              <option value="Etransfer">E-transfer</option>
              <option value="Par">PAR</option>
              <option value="CreditCard">Credit Card</option>
              <option value="MonerisPinPad">Moneris PinPad</option>
              <option value="ProgramCmte">Program Committee</option>
              <option value="BenevolentFund">Benevolent Fund</option>
              <option value="Other">Other</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label htmlFor="search">Search:</label>
            <input
              id="search"
              type="text"
              placeholder="Search by name or place code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="summary-section">
        <div className="summary-card">
          <h3>Total Records</h3>
          <p>{filteredRecords.length}</p>
        </div>
        <div className="summary-card">
          <h3>Total Amount</h3>
          <p>{formatCurrency(totalAmount)}</p>
        </div>
      </div>

      {/* Payment Type Breakdown */}
      <div className="payment-breakdown">
        <h3>Payment Type Breakdown</h3>
        <div className="breakdown-grid">
          {Object.entries(paymentTypeTotals).map(([method, amount]) => {
            const totalCCFees = getTotalCreditCardFees();
            const isCreditCard = method === 'CreditCard';
            return (
              <div 
                key={method} 
                className={`breakdown-item ${selectedPaymentType === method ? 'active' : ''}`}
                onClick={() => setSelectedPaymentType(selectedPaymentType === method ? 'all' : method as PaymentMethod)}
                style={{ cursor: 'pointer' }}
                title={`Click to filter by ${getPaymentMethodLabel(method as PaymentMethod)}`}
              >
                <span className="method-label">{getPaymentMethodLabel(method as PaymentMethod)}:</span>
                <span className="method-amount">
                  {formatCurrency(amount)}
                  {isCreditCard && totalCCFees > 0 && (
                    <span style={{ fontSize: '0.8em', marginLeft: '4px', opacity: 0.8 }}>
                      (incl {formatCurrency(totalCCFees)} fee)
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Payment Records Table */}
      <div className="table-container">
        <table className="payment-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('name')}>
                Primary Camper {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('amount')}>
                Place Code {sortField === 'amount' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('amount')}>
                Amount {sortField === 'amount' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('method')}>
                Payment Type {sortField === 'method' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('date')}>
                Date {sortField === 'date' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {filteredRecords.map(record => (
              <tr 
                key={record.id}
                onClick={() => handleRowClick(record)}
                className="clickable-row"
              >
                <td>{record.firstName} {record.lastName}</td>
                <td className="place-code-col">{record.placeCode}</td>
                <td className="amount-col">{formatAmountWithFee(record)}</td>
                <td>{getPaymentMethodLabel(record.method)}</td>
                <td>{formatDate(record.paymentDate)}</td>
                <td>{record.notes || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredRecords.length === 0 && (
        <div className="no-records">
          No payment records found matching the current filters.
        </div>
      )}
    </div>
  );
};

export default PaymentRecords; 